import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { buildSmsUrl } from "../lib/sms";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  User, 
  Target, 
  FileText, 
  Plus, 
  Trash2,
  IdCard,
  Phone,
  Award,
  Wallet,
  AlertTriangle,
  Receipt,
  Camera,
  MapPin,
  Scale,
  Image as ImageIcon,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Calendar,
  Users,
  Gift,
  Shield,
  X
} from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import AdminSettingsSection from "../components/AdminSettingsSection";
import NotificationPrefs from "../components/NotificationPrefs";
import { formatSloDate, yearsSince } from "../lib/dateUtils";

const ProfilePage = () => {
  const { user, token, hasRole } = useAuth();
  const location = useLocation();
  const initialTab = (() => {
    const p = new URLSearchParams(location.search);
    const t = p.get("tab");
    return ["ure", "odstrel", "dolgovi", "delo"].includes(t) ? t : "ure";
  })();
  const [notes, setNotes] = useState([]);
  const [huntedAnimals, setHuntedAnimals] = useState([]);
  const [debts, setDebts] = useState([]);
  const [debtSummary, setDebtSummary] = useState({ skupni_dolg: 0, clanarina: 0, odkup_divjacine: 0, drugo: 0 });
  const [animalCategories, setAnimalCategories] = useState([]);
  const [workHours, setWorkHours] = useState([]);
  const [hoursSummary, setHoursSummary] = useState({ obvezne_ure: 20, opravljene_ure: 0 });
  const [permissionsCatalog, setPermissionsCatalog] = useState([]);
  const [myVezice, setMyVezice] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [huntedDialogOpen, setHuntedDialogOpen] = useState(false);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [donateDialogOpen, setDonateDialogOpen] = useState(false);
  const [donateForm, setDonateForm] = useState({ za_clan_id: "", ure: 1, opis: "" });
  const [smsNotifyData, setSmsNotifyData] = useState(null); // {sms_besedilo, phones}
  const [noteForm, setNoteForm] = useState({ naslov: "", vsebina: "" });
  const [huntedForm, setHuntedForm] = useState({
    zival_id: "",
    zival_naziv: "",
    datum: new Date().toISOString().split('T')[0],
    cas: "",
    spol: "",
    starost: "",
    lokacija: "",
    teza_bruto: "",
    teza_neto: "",
    teza_oddaja: "",
    opombe: "",
    slika_url: "",
    vezica_id: "",
  });
  const [hoursForm, setHoursForm] = useState({
    datum: new Date().toISOString().split('T')[0],
    ure: 1,
    tip: "delovna_akcija",
    opis: "",
    orodje: "brez",
    za_clan_id: "self"
  });
  const [membersList, setMembersList] = useState([]);

  const orodjaOptions = [
    { value: "brez", label: "Brez orodja (ročno delo)", faktor: 1 },
    { value: "motorna_zaga", label: "Motorna žaga", faktor: 2 },
    { value: "kosilnica", label: "Kosilnica", faktor: 2 },
    { value: "traktor", label: "Traktor", faktor: 3 },
    { value: "delovni_stroj", label: "Delovni stroj", faktor: 3 },
  ];
  const getOrodjeFaktor = (o) => orodjaOptions.find(x => x.value === o)?.faktor || 1;
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  useEffect(() => {
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [notesRes, huntedRes, debtsRes, debtSummaryRes, categoriesRes, hoursRes, hoursSummaryRes] = await Promise.all([
        axios.get(`${API}/notes`, { headers }),
        axios.get(`${API}/hunted`, { headers }),
        axios.get(`${API}/debts/my`, { headers }),
        axios.get(`${API}/debts/my/summary`, { headers }),
        axios.get(`${API}/animal-categories`, { headers }),
        axios.get(`${API}/hours/my`, { headers }),
        axios.get(`${API}/hours/summary`, { headers })
      ]);
      setNotes(notesRes.data);
      setHuntedAnimals(huntedRes.data);
      setDebts(debtsRes.data);
      setDebtSummary(debtSummaryRes.data);
      setAnimalCategories(categoriesRes.data);
      setWorkHours(hoursRes.data);
      setHoursSummary(hoursSummaryRes.data);
      // Fetch members list for "za člana" dropdown
      try {
        const membersRes = await axios.get(`${API}/users/directory`, { headers });
        setMembersList(membersRes.data);
      } catch (err) {
        console.warn("[ProfilePage] Failed to fetch members directory:", err);
      }
      // Fetch permissions catalog (labels for user's granular permissions)
      try {
        const permsRes = await axios.get(`${API}/permissions/catalog`, { headers });
        setPermissionsCatalog(permsRes.data || []);
      } catch (err) {
        console.warn("[ProfilePage] Failed to fetch permissions catalog:", err);
      }
      // Fetch vezice assigned to current user
      try {
        if (user?.id) {
          const vezRes = await axios.get(`${API}/vezice/by-user/${user.id}`, { headers });
          setMyVezice(vezRes.data || []);
        }
      } catch (err) {
        console.warn("[ProfilePage] Failed to fetch vezice:", err);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/notes`, noteForm, { headers });
      toast.success("Zapisek dodan");
      setNoteDialogOpen(false);
      setNoteForm({ naslov: "", vsebina: "" });
      fetchUserData();
    } catch (error) {
      toast.error("Napaka pri shranjevanju");
    }
  };

  const handleDeleteNote = async (id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/notes/${id}`, { headers });
      toast.success("Zapisek izbrisan");
      fetchUserData();
    } catch (error) {
      toast.error("Napaka pri brisanju");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    setUploadingImage(true);
    try {
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/image`, formData, { headers });
      setHuntedForm(prev => ({ ...prev, slika_url: response.data.url }));
      toast.success("Slika naložena");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri nalaganju slike");
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleCreateHunted = async (e) => {
    e.preventDefault();
    if (!huntedForm.zival_id) {
      toast.error("Izberite vrsto živali");
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        ...huntedForm,
        teza_bruto: huntedForm.teza_bruto ? parseFloat(huntedForm.teza_bruto) : null,
        teza_neto: huntedForm.teza_neto ? parseFloat(huntedForm.teza_neto) : null,
        teza_oddaja: huntedForm.teza_oddaja ? parseFloat(huntedForm.teza_oddaja) : null,
        teza: huntedForm.teza_bruto ? parseFloat(huntedForm.teza_bruto) : null  // legacy mirror
      };
      const res = await axios.post(`${API}/hunted`, data, { headers });
      toast.success("Odstrel dodan");
      setHuntedDialogOpen(false);
      resetHuntedForm();
      fetchUserData();
      
      // Offer SMS notification
      if (res.data.sms_besedilo) {
        try {
          const phonesRes = await axios.get(`${API}/hunted/sms-phones`, { headers });
          setSmsNotifyData({
            sms_besedilo: res.data.sms_besedilo,
            phones: phonesRes.data.phones || []
          });
        } catch (err) {
          console.warn("[ProfilePage] SMS phones lookup failed:", err);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  // Send SMS notification about harvest (iOS-safe URL builder)
  const handleSendHuntSMS = () => {
    if (!smsNotifyData) return;
    const { sms_besedilo, phones } = smsNotifyData;
    if (phones.length > 0) {
      window.location.href = buildSmsUrl(phones, sms_besedilo);
    }
    toast.success("SMS aplikacija odprta");
    setSmsNotifyData(null);
  };

  const handleSendHuntSMSIndividually = () => {
    if (!smsNotifyData) return;
    const { sms_besedilo, phones } = smsNotifyData;
    if (phones.length > 0) {
      window.location.href = buildSmsUrl([phones[0]], sms_besedilo);
      toast.info(`SMS 1/${phones.length}`);
    }
    setSmsNotifyData(null);
  };

  const handleDeleteHunted = async (id) => {
    if (!confirm("Ali ste prepričani?")) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hunted/${id}`, { headers });
      toast.success("Zapis izbrisan");
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju");
    }
  };

  const handleCreateHours = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...hoursForm,
        za_clan_id: hoursForm.za_clan_id === "self" ? "" : hoursForm.za_clan_id,
      };
      await axios.post(`${API}/hours`, payload, { headers });
      toast.success(
        hoursForm.za_clan_id && hoursForm.za_clan_id !== "self"
          ? "Ure zabeležene za izbranega člana - čakajo na potrditev"
          : "Ure zabeležene - čakajo na potrditev"
      );
      setHoursDialogOpen(false);
      setHoursForm({
        datum: new Date().toISOString().split('T')[0],
        ure: 1,
        tip: "delovna_akcija",
        opis: "",
        orodje: "brez",
        za_clan_id: "self"
      });
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri shranjevanju");
    }
  };

  const handleDonateHours = async (e) => {
    e.preventDefault();
    if (!donateForm.za_clan_id) {
      toast.error("Izberite člana");
      return;
    }
    if (!donateForm.ure || donateForm.ure <= 0) {
      toast.error("Vnesite veljavno število ur");
      return;
    }
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/hours/podari`, {
        za_clan_id: donateForm.za_clan_id,
        ure: parseFloat(donateForm.ure),
        opis: donateForm.opis,
      }, { headers });
      toast.success("Ure podarjene - čakajo na potrditev vodstva");
      setDonateDialogOpen(false);
      setDonateForm({ za_clan_id: "", ure: 1, opis: "" });
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri darovanju");
    }
  };

  const handleDeleteHours = async (hoursId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/hours/${hoursId}`, { headers });
      toast.success("Ure izbrisane");
      fetchUserData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Napaka pri brisanju - samo nepotrjene ure se lahko brišejo");
    }
  };

  const resetHuntedForm = () => {
    setHuntedForm({
      zival_id: "",
      zival_naziv: "",
      datum: new Date().toISOString().split('T')[0],
      cas: "",
      spol: "",
      starost: "",
      lokacija: "",
      teza_bruto: "",
      teza_neto: "",
      teza_oddaja: "",
      opombe: "",
      slika_url: "",
      vezica_id: "",
    });
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const detectSpolFromName = (name) => {
    if (!name) return "";
    const n = name.toLowerCase().trim();
    // Explicit suffix markers (e.g. "Tele M", "Mladič Ž") — handle first
    // Note: \b doesn't work reliably with non-ASCII like "ž", so use (^|\s) anchor
    if (/(^|\s)(ž|ž\.|ženska|zenska)\s*$/.test(n)) return "Z";
    if (/(^|\s)(m|m\.|moški|moski)\s*$/.test(n)) return "M";
    // Males
    const maleKeywords = ["srnjak", "jelen", "merjasec", "gumbar", "oven", "kozel", "petelin", "racak"];
    if (maleKeywords.some(k => n.includes(k))) return "M";
    // Females
    const femaleKeywords = ["košuta", "kosuta", "junica", "srna", "svinja", "ovca", "koza", "mladica", "kokoš", "raca"];
    if (femaleKeywords.some(k => n.includes(k))) return "Z";
    // Otherwise leave empty (e.g. mladiči, ozimci, gams, jazbec, lisica, šoja...)
    return "";
  };

  const handleAnimalSelect = (categoryId, subcategoryId, subcategoryName) => {
    const auto_spol = detectSpolFromName(subcategoryName);
    setHuntedForm(prev => ({
      ...prev,
      zival_id: subcategoryId,
      zival_naziv: subcategoryName,
      spol: auto_spol
    }));
  };

  // Compute composite Select value so the SelectItem visually matches the chosen sub-animal
  const getZivalSelectValue = () => {
    if (!huntedForm.zival_id) return "";
    for (const cat of animalCategories) {
      const sub = (cat.podkategorije || []).find(s => s.id === huntedForm.zival_id);
      if (sub) return `${cat.id}:::${sub.id}:::${sub.naziv}`;
    }
    return "";
  };

  const getRoleLabel = (role) => {
    const labels = {
      predsednik: "Predsednik",
      gospodar: "Gospodar",
      blagajnik: "Blagajnik",
      tajnik: "Tajnik",
      upravnik_lovske_koce: "Upravnik lovske koče",
      skupinovodja: "Skupinovodja",
      kinoloski_referent: "Kinološki referent",
      referent_strelstvo: "Referent za strelstvo",
      komisija_skode: "Komisija za škode",
      komisija_izobrazevanje: "Komisija za izobraževanje",
      komisija_trofeje: "Komisija za ocenjevanje trofej",
      disciplinska_komisija: "Disciplinska komisija",
      clan: "Član"
    };
    return labels[role] || role;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("sl-SI", { day: "numeric", month: "short", year: "numeric" });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sl-SI', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getHoursTypeLabel = (tip) => {
    const labels = {
      delovna_akcija: "Delovna akcija",
      skupni_lov: "Skupni lov",
      drugo: "Drugo"
    };
    return labels[tip] || tip;
  };

  const hoursPercentage = hoursSummary.obvezne_ure > 0 
    ? Math.min(100, Math.round((hoursSummary.opravljene_ure / hoursSummary.obvezne_ure) * 100))
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="profile-page">
      {/* SMS Notification Dialog for Harvest */}
      <Dialog open={!!smsNotifyData} onOpenChange={(open) => !open && setSmsNotifyData(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Zaključen plan odstrela</DialogTitle>
            <DialogDescription>
              Plan odstrela za to kategorijo je zaključen. Lahko obvestite člane preko SMS.
            </DialogDescription>
          </DialogHeader>
          {smsNotifyData && (
            <div className="space-y-4">
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium mb-1">Besedilo SMS:</p>
                <p className="text-muted-foreground">{smsNotifyData.sms_besedilo}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                {smsNotifyData.phones.length} prejemnikov s telefonsko številko
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSmsNotifyData(null)}>
              Preskoči
            </Button>
            <Button onClick={handleSendHuntSMSIndividually} variant="secondary" data-testid="sms-individual-btn">
              <Phone className="h-4 w-4 mr-1" />
              Pošlji posamično
            </Button>
            <Button onClick={handleSendHuntSMS} data-testid="sms-all-btn">
              <Send className="h-4 w-4 mr-1" />
              Pošlji vsem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-primary flex items-center gap-2">
          <User className="h-6 w-6 sm:h-8 sm:w-8" />
          Moj profil
        </h1>
      </div>

      {/* Birthday greeting */}
      {user?.rojstni_dan && (() => {
        const today = new Date();
        const todayMD = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const rd = user.rojstni_dan;
        const rdMD = rd.length > 5 ? rd.substring(5) : rd;
        if (todayMD === rdMD) {
          const birthYear = parseInt(rd.substring(0, 4));
          const age = today.getFullYear() - birthYear;
          return (
            <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="birthday-greeting">
              <CardContent className="py-4 text-center">
                <div className="text-3xl mb-2">🎂🎉</div>
                <h3 className="text-lg font-bold text-amber-900">
                  Vse najboljše za {age}. rojstni dan, {user.spol === "Z" ? "draga" : "dragi"} {user.ime}!
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  Lovska družina Rakek ti želi vse dobro in Dober pogled app in mirno roko!
                </p>
              </CardContent>
            </Card>
          );
        }
        return null;
      })()}

      {/* Profile Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">
                  {user?.ime?.[0]}{user?.priimek?.[0]}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold">{user?.ime} {user?.priimek}</h2>
              <Badge className="mt-1">{getRoleLabel(user?.vloga)}</Badge>
              <div className="grid grid-cols-2 gap-2 mt-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <IdCard className="h-3 w-3" />
                  <span>{user?.lovska_izkaznica}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Award className="h-3 w-3" />
                  <span>Član #{user?.stevilka_clana}</span>
                </div>
                {user?.telefon && (
                  <div className="flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    <span>{user?.telefon}</span>
                  </div>
                )}
                {user?.rojstni_dan && formatSloDate(user.rojstni_dan) && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    <span>Roj: {formatSloDate(user.rojstni_dan)}
                      {(() => {
                        const age = yearsSince(user.rojstni_dan);
                        return age != null ? ` (${age} let)` : "";
                      })()}
                    </span>
                  </div>
                )}
                {user?.datum_vstopa && formatSloDate(user.datum_vstopa) && (
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span>V LD od {formatSloDate(user.datum_vstopa)}
                      {(() => {
                        const years = yearsSince(user.datum_vstopa);
                        return years != null ? ` (${years} let)` : "";
                      })()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Granular Permissions (only shown if user has any) */}
      {(user?.permissions?.length > 0) && (
        <Card className="border-primary/20 bg-primary/5" data-testid="my-permissions-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Moja posebna dovoljenja
            </CardTitle>
            <CardDescription>
              Predsednik ti je dodelil naslednje dodatne pravice v aplikaciji:
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {user.permissions.map(pKey => {
                const label = permissionsCatalog.find(p => p.key === pKey)?.label || pKey;
                return (
                  <Badge
                    key={pKey}
                    variant="outline"
                    className="bg-white border-primary/30 text-primary py-1.5 px-3 gap-1.5"
                    data-testid={`my-permission-${pKey}`}
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    {label}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-uporabnik notifikacijske preference (drsniki) */}
      <NotificationPrefs token={token} />

      {/* Moje vezice */}
      {myVezice.length > 0 && (
        <Card data-testid="my-vezice-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Award className="h-5 w-5" />
              Moje vezice ({myVezice.length})
            </CardTitle>
            <CardDescription>Zelena = neuporabljena · Rdeča = uporabljena</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {myVezice.map(v => {
                const isUsed = v.status === "uporabljena";
                return (
                  <span
                    key={v.id}
                    data-testid={`my-vezica-${v.id}`}
                    className={`inline-block px-3 py-1.5 rounded-md font-mono text-sm font-semibold border ${
                      isUsed
                        ? "bg-red-100 text-red-800 border-red-300"
                        : "bg-green-100 text-green-800 border-green-300"
                    }`}
                    title={`${isUsed ? "Uporabljena" : "Neuporabljena"}${v.leto ? ` · ${v.leto}` : ""}`}
                  >
                    {v.stevilka}
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Hours Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Opravljene ure
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-2">
            {hoursSummary.obvezne_ure > 0 ? (
              <span className="text-2xl font-bold">
                {hoursSummary.opravljene_ure} / {hoursSummary.obvezne_ure} ur
              </span>
            ) : (
              <span className="text-2xl font-bold text-green-600">
                +{hoursSummary.opravljene_ure} ur
              </span>
            )}
            <Badge variant={hoursSummary.obvezne_ure === 0 ? "secondary" : hoursPercentage >= 100 ? "default" : "outline"}>
              {hoursSummary.obvezne_ure === 0 ? "Opravičen" : `${hoursPercentage}%`}
            </Badge>
          </div>
          {hoursSummary.obvezne_ure > 0 && (
            <div className="quota-progress h-3">
              <div 
                className={`quota-progress-bar ${hoursPercentage >= 100 ? 'bg-green-500' : hoursPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                style={{ width: `${hoursPercentage}%` }}
              />
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            {hoursSummary.obvezne_ure === 0 
              ? (hoursSummary.opravljene_ure > 0 ? `${hoursSummary.opravljene_ure} ur opravljenih v dobro družine` : "Opravičen obveznih ur")
              : hoursPercentage >= 100 
                ? "Obvezne ure so opravljene!" 
                : `Še ${hoursSummary.obvezne_ure - hoursSummary.opravljene_ure} ur do cilja`}
          </p>
        </CardContent>
      </Card>

      {/* Finance Summary */}
      <Card className={debtSummary.skupni_dolg > 0 ? "border-red-200 bg-red-50/50" : "border-green-200 bg-green-50/50"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            Finančno stanje
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center">
            <p className={`text-3xl font-bold ${debtSummary.skupni_dolg > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(debtSummary.skupni_dolg)}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {debtSummary.skupni_dolg > 0 ? "Skupni dolg" : "Brez obveznosti"}
            </p>
          </div>
          {debtSummary.skupni_dolg > 0 && (
            <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Poravnajte dolg pri blagajniku
            </div>
          )}
        </CardContent>
      </Card>

      {/* Theme selector (per-user) */}
      <ThemeToggle />

      {/* Admin-only settings (push events, vezice) */}
      <AdminSettingsSection />

      {/* Tabs */}
      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 h-auto">
          <TabsTrigger value="ure" className="text-xs px-2 py-2">
            <Clock className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Ure</span>
          </TabsTrigger>
          <TabsTrigger value="odstrel" className="text-xs px-2 py-2">
            <Target className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Odstrel</span>
          </TabsTrigger>
          <TabsTrigger value="dolgovi" className="text-xs px-2 py-2">
            <Receipt className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Dolgovi</span>
          </TabsTrigger>
          <TabsTrigger value="zapiski" className="text-xs px-2 py-2">
            <FileText className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Zapiski</span>
          </TabsTrigger>
        </TabsList>

        {/* Work Hours Tab */}
        <TabsContent value="ure" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2 gap-2 flex-wrap">
              <CardTitle className="text-base">Beleženje ur</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDonateDialogOpen(true)}
                  data-testid="open-donate-hours"
                >
                  <Gift className="h-4 w-4 mr-1" />
                  Podari ure
                </Button>
                <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
                  <DialogTrigger asChild>
                    <Button size="sm">
                      <Plus className="h-4 w-4 mr-1" />
                      Dodaj
                    </Button>
                  </DialogTrigger>
                <DialogContent className="max-w-sm mx-4">
                  <DialogHeader>
                    <DialogTitle>Beleži ure</DialogTitle>
                    <DialogDescription>
                      Ure bodo potrjene s strani gospodarja
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateHours} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Tip</Label>
                        <Select 
                          onValueChange={(value) => setHoursForm({ ...hoursForm, tip: value })} 
                          value={hoursForm.tip}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="delovna_akcija">Delovna akcija</SelectItem>
                            <SelectItem value="skupni_lov">Skupni lov</SelectItem>
                            <SelectItem value="drugo">Drugo</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Orodje</Label>
                        <Select 
                          onValueChange={(value) => setHoursForm({ ...hoursForm, orodje: value })} 
                          value={hoursForm.orodje}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {orodjaOptions.map(o => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label} (x{o.faktor})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Datum</Label>
                        <Input
                          type="date"
                          value={hoursForm.datum}
                          onChange={(e) => setHoursForm({ ...hoursForm, datum: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Dejanske ure</Label>
                        <Input
                          type="number"
                          step="0.5"
                          min="0.5"
                          value={hoursForm.ure}
                          onChange={(e) => setHoursForm({ ...hoursForm, ure: parseFloat(e.target.value) || 1 })}
                          required
                        />
                      </div>
                    </div>
                    {getOrodjeFaktor(hoursForm.orodje) > 1 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-sm text-blue-800">
                        {hoursForm.ure}h x {getOrodjeFaktor(hoursForm.orodje)} = <strong>{(hoursForm.ure * getOrodjeFaktor(hoursForm.orodje)).toFixed(1)}h</strong> pomnoženih ur
                      </div>
                    )}
                    <div className="space-y-1">
                      <Label className="text-xs">Opis</Label>
                      <Input
                        value={hoursForm.opis}
                        onChange={(e) => setHoursForm({ ...hoursForm, opis: e.target.value })}
                        placeholder="Kratek opis dela"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Pripiši ure drugemu članu (opcijsko)</Label>
                      <Select
                        value={hoursForm.za_clan_id || "self"}
                        onValueChange={(value) => setHoursForm({ ...hoursForm, za_clan_id: value })}
                      >
                        <SelectTrigger
                          data-testid="hours-za-clan-select"
                          className="h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left"
                        >
                          <SelectValue placeholder="Zase (privzeto)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Zase</SelectItem>
                          {membersList
                            .filter(m => m.id !== user?.id)
                            .map(m => (
                              <SelectItem key={m.id} value={m.id} className="whitespace-normal break-words">
                                {m.ime} {m.priimek}
                              </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {hoursForm.za_clan_id && hoursForm.za_clan_id !== "self" && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-sm text-amber-800">
                        Ure bodo pripisane izbranemu članu. Potrebna je potrditev vodstva.
                      </div>
                    )}
                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => setHoursDialogOpen(false)}>
                        Prekliči
                      </Button>
                      <Button type="submit">Shrani</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {workHours.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Ni zabeleženih ur</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {workHours.map((entry) => (
                    <div 
                      key={entry.id}
                      className={`p-3 rounded-lg border ${entry.potrjeno ? 'bg-green-50/50 border-green-200' : 'bg-amber-50/50 border-amber-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {getHoursTypeLabel(entry.tip)}
                            </Badge>
                            {entry.orodje && entry.orodje !== "brez" && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                {orodjaOptions.find(o => o.value === entry.orodje)?.label || entry.orodje} x{entry.faktor || 1}
                              </Badge>
                            )}
                            <span className="font-semibold">
                              {entry.ure} ur
                              {entry.dejanske_ure && entry.dejanske_ure !== entry.ure && (
                                <span className="text-xs text-muted-foreground font-normal ml-1">({entry.dejanske_ure} dej.)</span>
                              )}
                            </span>
                            {entry.potrjeno ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <Clock className="h-4 w-4 text-amber-600" />
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{entry.opis}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(entry.datum)}
                            {entry.potrjeno && entry.potrdil_ime && ` • Potrdil: ${entry.potrdil_ime}`}
                          </p>
                        </div>
                        {!entry.potrjeno && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive"
                            onClick={() => handleDeleteHours(entry.id)}
                            title="Izbriši (samo nepotrjene)"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hunted Animals Tab */}
        <TabsContent value="odstrel" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Moj odstrel</CardTitle>
              <Dialog open={huntedDialogOpen} onOpenChange={(open) => { setHuntedDialogOpen(open); if (!open) resetHuntedForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Dodaj
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Nov odstrel</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateHunted} className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Vrsta živali *</Label>
                      <Select
                        value={getZivalSelectValue()}
                        onValueChange={(val) => {
                          // val format: "categoryId:::subId:::subName"
                          const [categoryId, subId, subName] = val.split(":::");
                          handleAnimalSelect(categoryId, subId, subName);
                        }}
                      >
                        <SelectTrigger
                          data-testid="odstrel-zival-select"
                          className="h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left"
                        >
                          <SelectValue placeholder="Izberi divjad" />
                        </SelectTrigger>
                        <SelectContent>
                          {animalCategories.map((category) => (
                            <div key={category.id}>
                              <div className="px-2 py-1 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {category.naziv}
                              </div>
                              {category.podkategorije.map((sub) => (
                                <SelectItem
                                  key={sub.id}
                                  value={`${category.id}:::${sub.id}:::${sub.naziv}`}
                                  className="whitespace-normal break-words"
                                >
                                  {sub.naziv}
                                </SelectItem>
                              ))}
                            </div>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Spol</Label>
                        <Select
                          value={huntedForm.spol || "none"}
                          onValueChange={(v) => setHuntedForm({ ...huntedForm, spol: v === "none" ? "" : v })}
                        >
                          <SelectTrigger data-testid="odstrel-spol-select">
                            <SelectValue placeholder="Izberi" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">—</SelectItem>
                            <SelectItem value="M">Moški</SelectItem>
                            <SelectItem value="Z">Ženska</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Starost (let)</Label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          max="30"
                          placeholder="npr. 3"
                          value={huntedForm.starost}
                          onChange={(e) => setHuntedForm({ ...huntedForm, starost: e.target.value })}
                          data-testid="odstrel-starost-input"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Datum *</Label>
                        <Input
                          type="date"
                          value={huntedForm.datum}
                          onChange={(e) => setHuntedForm({ ...huntedForm, datum: e.target.value })}
                          required
                          data-testid="odstrel-datum"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Čas odstrela</Label>
                        <Input
                          type="time"
                          value={huntedForm.cas}
                          onChange={(e) => setHuntedForm({ ...huntedForm, cas: e.target.value })}
                          data-testid="odstrel-cas"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Teža bruto (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="60"
                          value={huntedForm.teza_bruto}
                          onChange={(e) => setHuntedForm({ ...huntedForm, teza_bruto: e.target.value })}
                          data-testid="odstrel-teza-bruto"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Teža neto (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="45"
                          value={huntedForm.teza_neto}
                          onChange={(e) => setHuntedForm({ ...huntedForm, teza_neto: e.target.value })}
                          data-testid="odstrel-teza-neto"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Za oddajo (kg)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          placeholder="40"
                          value={huntedForm.teza_oddaja}
                          onChange={(e) => setHuntedForm({ ...huntedForm, teza_oddaja: e.target.value })}
                          data-testid="odstrel-teza-oddaja"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Lokacija</Label>
                      <Input
                        placeholder="npr. Gozd pri Rakeku"
                        value={huntedForm.lokacija}
                        onChange={(e) => setHuntedForm({ ...huntedForm, lokacija: e.target.value })}
                      />
                    </div>

                    {/* Vezica dropdown – prikaži samo neuporabljene, dodeljene lovcu */}
                    {myVezice.filter(v => v.status !== "uporabljena").length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs">Vezica (opcijsko)</Label>
                        <select
                          className="w-full border rounded-md h-10 px-3 text-sm"
                          value={huntedForm.vezica_id || ""}
                          onChange={(e) => setHuntedForm({ ...huntedForm, vezica_id: e.target.value })}
                          data-testid="odstrel-vezica"
                        >
                          <option value="">— ne uporabi vezice —</option>
                          {myVezice
                            .filter(v => v.status !== "uporabljena")
                            .map(v => (
                              <option key={v.id} value={v.id}>{v.stevilka}{v.leto ? ` (${v.leto})` : ""}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-muted-foreground">Izbrana vezica bo označena kot <b>uporabljena</b> (rdeča).</p>
                      </div>
                    )}

                    <div className="space-y-1">
                      <Label className="text-xs">Opombe</Label>
                      <Textarea
                        placeholder="Dodatne opombe..."
                        value={huntedForm.opombe}
                        onChange={(e) => setHuntedForm({ ...huntedForm, opombe: e.target.value })}
                        rows={2}
                      />
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs">Slika živali</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1"
                          data-testid="odstrel-galerija"
                        >
                          <ImageIcon className="h-4 w-4 mr-1" />
                          Galerija
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => cameraInputRef.current?.click()}
                          className="flex-1"
                          data-testid="odstrel-foto"
                        >
                          <Camera className="h-4 w-4 mr-1" />
                          Fotografiraj
                        </Button>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                      {(imagePreview || uploadingImage) && (
                        <div className="border rounded-lg p-2 mt-2 flex items-center gap-2">
                          {imagePreview && (
                            <img src={imagePreview} alt="Predogled" className="h-20 w-20 object-cover rounded" />
                          )}
                          <div className="text-xs text-muted-foreground flex-1">
                            {uploadingImage ? "Nalagam..." : "Slika pripravljena"}
                          </div>
                          {imagePreview && !uploadingImage && (
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-600"
                              onClick={() => { setImagePreview(null); setHuntedForm({ ...huntedForm, slika_url: "" }); if (fileInputRef.current) fileInputRef.current.value = ''; if (cameraInputRef.current) cameraInputRef.current.value = ''; }}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => { setHuntedDialogOpen(false); resetHuntedForm(); }}>
                        Prekliči
                      </Button>
                      <Button type="submit" disabled={uploadingImage}>Shrani</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {huntedAnimals.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Ni evidentiranega odstrela</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {huntedAnimals.map((animal) => (
                    <div key={animal.id} className="p-3 rounded-lg border bg-card">
                      <div className="flex gap-3">
                        {animal.slika_url ? (
                          <img 
                            src={`${API}${animal.slika_url}`}
                            alt={animal.zival_naziv}
                            className="w-16 h-16 rounded object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            <ImageIcon className="h-6 w-6 text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h4 className="font-semibold break-words">
                                {animal.zival_naziv}
                                {animal.spol && <span className="ml-1 text-xs text-muted-foreground">({animal.spol === "M" ? "M" : "Ž"}{animal.starost ? `, ${animal.starost} let` : ""})</span>}
                              </h4>
                              <p className="text-xs text-muted-foreground">
                                {formatDate(animal.datum)}
                                {animal.cas && ` ob ${animal.cas}`}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive shrink-0"
                              onClick={() => handleDeleteHunted(animal.id)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-muted-foreground">
                            {animal.lokacija && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />{animal.lokacija}
                              </span>
                            )}
                            {(animal.teza_bruto || animal.teza) && (
                              <span className="flex items-center gap-1">
                                <Scale className="h-3 w-3" />Bruto: {animal.teza_bruto || animal.teza} kg
                              </span>
                            )}
                            {animal.teza_neto && (
                              <span>Neto: {animal.teza_neto} kg</span>
                            )}
                            {animal.teza_oddaja && (
                              <span>Oddaja: {animal.teza_oddaja} kg</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Debts Tab */}
        <TabsContent value="dolgovi" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Evidenca obveznosti</CardTitle>
            </CardHeader>
            <CardContent>
              {debts.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="h-10 w-10 text-green-500/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nimate odprtih obveznosti</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {debts.map((debt) => (
                    <div 
                      key={debt.id} 
                      className={`p-3 rounded-lg border ${debt.znesek > 0 ? 'bg-red-50/50 border-red-200' : 'bg-green-50/50 border-green-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium">{debt.opis}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(debt.datum)}</p>
                        </div>
                        <p className={`font-bold ${debt.znesek > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {debt.znesek > 0 ? '+' : ''}{formatCurrency(debt.znesek)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="zapiski" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-base">Moji zapiski</CardTitle>
              <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-1" />
                    Dodaj
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm mx-4">
                  <DialogHeader>
                    <DialogTitle>Nov zapisek</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateNote} className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Naslov</Label>
                      <Input
                        value={noteForm.naslov}
                        onChange={(e) => setNoteForm({ ...noteForm, naslov: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Vsebina</Label>
                      <Textarea
                        value={noteForm.vsebina}
                        onChange={(e) => setNoteForm({ ...noteForm, vsebina: e.target.value })}
                        rows={4}
                        required
                      />
                    </div>
                    <DialogFooter className="gap-2">
                      <Button type="button" variant="outline" onClick={() => setNoteDialogOpen(false)}>
                        Prekliči
                      </Button>
                      <Button type="submit">Shrani</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {notes.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Ni zapiskov</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notes.map((note) => (
                    <div key={note.id} className="p-3 rounded-lg border bg-card">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{note.naslov}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{note.vsebina}</p>
                          <p className="text-xs text-muted-foreground mt-2">{formatDate(note.ustvarjeno)}</p>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Donate Hours Dialog */}
      <Dialog open={donateDialogOpen} onOpenChange={setDonateDialogOpen}>
        <DialogContent className="max-w-sm mx-4" data-testid="donate-hours-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Podari ure
            </DialogTitle>
            <DialogDescription>
              Podarite lahko le <strong>presežek nad obvezo</strong>. Vodstvo mora potrditi.
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const opravljene = hoursSummary.opravljene_ure || 0;
            const obvezne = hoursSummary.obvezne_ure || 0;
            const presezek = Math.max(0, opravljene - obvezne);
            return (
              <form onSubmit={handleDonateHours} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 text-xs text-blue-800 space-y-0.5">
                  <div>Opravljeno: <strong>{opravljene}h</strong> • Obveza: <strong>{obvezne}h</strong></div>
                  <div>Razpoložljivo za darovanje: <strong className={presezek > 0 ? "text-green-700" : "text-red-700"}>{presezek}h</strong></div>
                </div>
                {presezek <= 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                    Najprej izpolnite svojo obvezo, šele nato lahko podarite presežek.
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Komu podariš</Label>
                  <Select
                    value={donateForm.za_clan_id}
                    onValueChange={(v) => setDonateForm({ ...donateForm, za_clan_id: v })}
                  >
                    <SelectTrigger
                      data-testid="donate-target-select"
                      className="h-auto min-h-9 py-1.5 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left"
                    >
                      <SelectValue placeholder="Izberi člana" />
                    </SelectTrigger>
                    <SelectContent>
                      {membersList
                        .filter(m => m.id !== user?.id)
                        .map(m => (
                          <SelectItem key={m.id} value={m.id} className="whitespace-normal break-words">
                            {m.ime} {m.priimek}
                          </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Število ur (max {presezek}h)</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    max={presezek}
                    value={donateForm.ure}
                    onChange={(e) => setDonateForm({ ...donateForm, ure: parseFloat(e.target.value) || 0 })}
                    data-testid="donate-hours-input"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Opomba (opcijsko)</Label>
                  <Input
                    value={donateForm.opis}
                    onChange={(e) => setDonateForm({ ...donateForm, opis: e.target.value })}
                    placeholder="Razlog za darovanje..."
                  />
                </div>
                <DialogFooter className="gap-2">
                  <Button type="button" variant="outline" onClick={() => setDonateDialogOpen(false)}>
                    Prekliči
                  </Button>
                  <Button
                    type="submit"
                    disabled={!donateForm.za_clan_id || donateForm.ure <= 0 || donateForm.ure > presezek}
                    data-testid="donate-hours-submit"
                  >
                    <Gift className="h-4 w-4 mr-1" />
                    Podari
                  </Button>
                </DialogFooter>
              </form>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
